import React, { Component } from "react";
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Popover,
  PopoverBody,
  PopoverHeader,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { post } from "helpers/api_helper";
import { toast } from "react-toastify";
import { Link } from "react-router-dom/cjs/react-router-dom";

class ChangePasswordModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      passwordStrength: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      },
      passwordHintPopoverOpen: false,
      passwordHintPopoverHoverTimeout: null,
      isTerms: false,
    };
    this.toggleTermsPolicy = this.toggleTermsPolicy.bind(this);
  }

  toggleTermsPolicy() {
    this.setState(prevState => ({
      isTerms: !prevState.isTerms
    }));
  }

  passwordHintPopoverShow = () => {
    if (this.state.passwordHintPopoverHoverTimeout) {
      clearTimeout(this.state.passwordHintPopoverHoverTimeout);
      this.setState({ passwordHintPopoverHoverTimeout: null });
    }
    this.setState({ passwordHintPopoverOpen: true });
  };

  passwordHintPopoverHide = () => {
    const timeout = setTimeout(() => {
      this.setState({ passwordHintPopoverOpen: false, passwordHintPopoverHoverTimeout: null });
    }, 100);
    this.setState({ passwordHintPopoverHoverTimeout: timeout });
  };

  checkPasswordStrength = (password) => {
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
  };

  handlePasswordChange = (e, setFieldValue) => {
    const password = e.target.value;
    const strength = this.checkPasswordStrength(password || '');
    this.setState({ passwordStrength: strength });
    setFieldValue('newPassword', password);
  };

  handleSubmit = async (values, { setSubmitting, resetForm }) => {
    toast.loading('Please wait...');
    try {
      const payload = {
        userId: parseInt(localStorage.getItem("userId"), 10),
        userName: values.userName,
        token: values.token,
        password: values.newPassword,
        oldPassword: values.oldPassword,
      };
      const response = await post(
        `user/changepasswordfirstlogin`,
        payload
      );
      if (response.success) {
        toast.dismiss();
        toast.success("Password changed successfully!");
        
        resetForm();
        this.setState({
          passwordStrength: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false,
          },
        });
        if (this.props.onPasswordChanged) {
          this.props.onPasswordChanged();
        }
        // Redirect to login page after successful password change
        window.location.href = "/login";
      } else {
        toast.dismiss();
        toast.warn(response.data?.validationErrors?.[0] || "Failed to change password");
      }
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error(
        error.response?.data?.message || "Failed to change password"
      );
    } finally {
      setSubmitting(false);
    }
  };

  render() {
    const { isOpen, userName, isRequired = false } = this.props;
    
    return (
      <>
      <Modal 
        isOpen={isOpen} 
        toggle={isRequired ? undefined : this.props.onClose}
        centered={true}
        backdrop={isRequired ? 'static' : true}
        keyboard={!isRequired}
        size="md"
      >
        <ModalHeader toggle={isRequired ? undefined : this.props.onClose}>
          Change Password
          {isRequired && (
            <span className="text-danger ms-2" style={{ fontSize: '0.9rem' }}>
              (Required)
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {isRequired && (
            <div className="alert alert-warning mb-3">
              <i className="mdi mdi-alert-circle me-2"></i>
              You are required to change your password before continuing.
            </div>
          )}
          <Formik
            initialValues={{
              userName: userName || "",
              oldPassword: "",
              newPassword: "",
              confirmPassword: "",
              acceptTerms: false,
            }}
            validationSchema={Yup.object().shape({
              oldPassword: Yup.string().required("Old password is required"),
              newPassword: Yup.string()
                .min(12, "New password must be at least 12 characters")
                .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
                .matches(/[a-z]/, "Password must contain at least one lowercase letter")
                .matches(/[0-9]/, "Password must contain at least one number")
                .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Password must contain at least one special character")
                .required("New password is required"),
              confirmPassword: Yup.string()
                .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
                .required("Confirm your new password"),
              acceptTerms: Yup.boolean()
                .oneOf([true], "You must accept the Terms of Use to continue"),
            })}
            onSubmit={this.handleSubmit}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form autoComplete="off">
               

                <div className="form-group mb-3">
                  <label htmlFor="oldPassword">Old Password</label>
                  <Field
                    type="password"
                    name="oldPassword"
                    className="form-control"
                    placeholder="Enter old password"
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="oldPassword"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="newPassword" className="d-flex align-items-center">
                    New Password
                    <span
                      className="btn btn-link btn-sm p-0 ms-1 text-muted"
                      style={{ fontSize: '1rem', textDecoration: 'none', cursor: 'pointer', lineHeight: 1 }}
                      onMouseEnter={this.passwordHintPopoverShow}
                      onMouseLeave={this.passwordHintPopoverHide}
                      role="button"
                      tabIndex={0}
                      title="Password requirements"
                    >
                      <i className="mdi mdi-information-outline" aria-hidden="true" />
                    </span>
                  </label>
                  <div id="passwordHintPopoverTarget" style={{ display: 'inline-block', width: '100%' }}>
                    <Field
                      type="password"
                      name="newPassword"
                      id="newPassword"
                      className="form-control"
                      placeholder="Enter new password (minimum 12 characters)"
                      autoComplete="off"
                      onChange={(e) => this.handlePasswordChange(e, setFieldValue)}
                      onFocus={this.passwordHintPopoverShow}
                      onBlur={this.passwordHintPopoverHide}
                    />
                    <Popover
                      placement="left"
                      isOpen={this.state.passwordHintPopoverOpen}
                      target="passwordHintPopoverTarget"
                      toggle={() => this.setState({ passwordHintPopoverOpen: !this.state.passwordHintPopoverOpen })}
                      style={{ fontSize: '14px' }}
                    >
                      <PopoverHeader style={{ fontSize: '14px' }}>Password Requirements</PopoverHeader>
                      <PopoverBody
                        onMouseEnter={this.passwordHintPopoverShow}
                        onMouseLeave={this.passwordHintPopoverHide}
                        style={{ fontSize: '14px' }}
                      >
                        <div className="password-requirements" style={{ fontSize: '14px' }}>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.length ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.length ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>Minimum 12 characters</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.uppercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.uppercase ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one uppercase letter (A-Z)</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.lowercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.lowercase ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one lowercase letter (a-z)</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.number ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.number ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one number (0-9)</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.special ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.special ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one special character (!@#$%^&*...)</span>
                          </div>
                        </div>
                      </PopoverBody>
                    </Popover>
                  </div>
                  <ErrorMessage
                    name="newPassword"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    className="form-control"
                    placeholder="Confirm new password"
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>

                <div className="form-check mb-3">
                  <Field
                    type="checkbox"
                    name="acceptTerms"
                    id="changePasswordAcceptTerms"
                    className="form-check-input"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="changePasswordAcceptTerms"
                  >
                    I have read and agree to the{" "}
                    <Link
                      to="#"
                      onClick={this.toggleTermsPolicy}
                    >
                      Terms of Use
                    </Link>
                    .
                  </label>
                  <ErrorMessage
                    name="acceptTerms"
                    component="div"
                    className="text-danger small mt-1 d-block"
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  {!isRequired && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={this.props.onClose}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || !values.acceptTerms}
                  >
                    {isSubmitting ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>

      <Offcanvas
                    isOpen={this.state.isTerms}
                    direction="end"
                    toggle={this.toggleTermsPolicy}
                    style={{ width: 600 }}
                >
                    <OffcanvasHeader toggle={this.toggleTermsPolicy}>
                       Terms of Use
                    </OffcanvasHeader>
                    <OffcanvasBody>
                    <div className="terms-container" style={{ padding: "20px", lineHeight: "1.6" }}>
      

      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          These Client Portal Terms of Use (“Terms”) govern your access to and use of the Realty Space Client Portal (the “Portal”), operated by 1978778 Ontario Inc. (“Realty Space”, “we”, “us”, or “our”).
        </p>
        <p>
          By accessing or using the Portal, you agree to be bound by these Terms. If you do not agree, you should not use the Portal.
        </p>
      </section>

      <hr />

      <section>
        <h2>2. Role of Realty Space</h2>
        <p>
          Realty Space provides a technology platform designed to facilitate communication and information sharing between real estate professionals and their clients.
        </p>
        <p>Realty Space is not:</p>
        <ul>
          <li>A real estate brokerage</li>
          <li>A real estate agent</li>
          <li>A legal advisor</li>
          <li>A financial or mortgage advisor</li>
        </ul>
        <p>
          Realty Space does not participate in, manage, or advise on real estate transactions.
        </p>
      </section>

      <hr />

      <section>
        <h2>3. Relationship with Your Real Estate Professional</h2>
        <p>The Portal is provided to you by your real estate agent or brokerage.</p>
        <ul>
          <li>Your relationship is solely with your agent or brokerage</li>
          <li>
            Realty Space is not a party to any agreement, transaction, or relationship between you and your agent
          </li>
        </ul>
        <p>
          All questions regarding listings, offers, transactions, or advice should be directed to your agent or brokerage.
        </p>
      </section>

      <hr />

      <section>
        <h2>4. Information Accuracy</h2>
        <p>
          Information displayed in the Portal, including property details, transaction information, and communications, is provided by your real estate professional.
        </p>
        <p>
          Realty Space does not verify the accuracy, completeness, or reliability of this information.
        </p>
      </section>

      <hr />

      <section>
        <h2>5. Use of the Portal</h2>
        <p>You agree to:</p>
        <ul>
          <li>Use the Portal for lawful purposes only</li>
          <li>Provide accurate information when requested</li>
          <li>Maintain the confidentiality of your login credentials</li>
        </ul>

        <p>You agree not to:</p>
        <ul>
          <li>Attempt to gain unauthorized access to the system</li>
          <li>Interfere with the operation of the Portal</li>
          <li>Use the Portal for fraudulent or unlawful purposes</li>
        </ul>
      </section>

      <hr />

      <section>
        <h2>6. Communications</h2>
        <p>By using the Portal, you may receive communications such as:</p>
        <ul>
          <li>Property updates</li>
          <li>Appointment notifications</li>
          <li>Transaction-related messages</li>
        </ul>
        <p>
          These communications are facilitated by Realty Space on behalf of your real estate professional.
        </p>
      </section>

      <hr />

      <section>
        <h2>7. No Professional Advice</h2>
        <p>
          The Portal may include tools, comparisons, or insights to assist you.
        </p>
        <p>
          These are provided for informational purposes only and do not constitute legal, financial, or real estate advice.
        </p>
        <p>
          You should consult your real estate agent or other professionals before making decisions.
        </p>
      </section>

      <hr />

      <section>
        <h2>8. Limitation of Liability</h2>
        <p>To the fullest extent permitted by law, Realty Space shall not be liable for:</p>
        <ul>
          <li>Decisions made based on information in the Portal</li>
          <li>Errors or omissions in content provided by your agent</li>
          <li>Any outcomes related to real estate transactions</li>
        </ul>
      </section>

      <hr />

      <section>
        <h2>9. Disclaimer of Warranties</h2>
        <p>
          The Portal is provided “as is” and “as available” without warranties of any kind.
        </p>
        <p>Realty Space does not guarantee:</p>
        <ul>
          <li>Continuous or uninterrupted access</li>
          <li>Accuracy of information</li>
          <li>Suitability for specific purposes</li>
        </ul>
      </section>

      <hr />

      <section>
        <h2>10. Privacy</h2>
        <p>
          Your use of the Portal is also governed by the Realty Space Privacy Policy.
        </p>
      </section>

      <hr />

      <section>
        <h2>11. Suspension or Termination</h2>
        <p>Access to the Portal may be suspended or terminated:</p>
        <ul>
          <li>By your real estate professional</li>
          <li>By Realty Space for security or misuse reasons</li>
        </ul>
      </section>

      <hr />

      <section>
        <h2>12. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Continued use of the Portal constitutes acceptance of the updated Terms.
        </p>
      </section>

      <hr />

      <section>
        <h2>13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Province of Ontario and applicable Canadian laws.
        </p>
      </section>

      <hr />

      <section>
        <h2>14. Contact Us</h2>
        <p>If you have questions about these Terms, please contact:</p>
        <p>Email: support@realtyspace.co</p>
        <p>Company: 1978778 Ontario Inc.</p>
        <p>Website: www.realtyspace.co</p>
      </section>
    </div>



                    </OffcanvasBody>
                </Offcanvas>
      </>
      
    );
  }
}

ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onPasswordChanged: PropTypes.func,
  userName: PropTypes.string,
  isRequired: PropTypes.bool,
};

export default ChangePasswordModal;
